import React, { useState } from 'react'
import PropTypes from 'prop-types'

import {
    Box,
    Text,
    Button,
    Flex,
    Skeleton,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    useDisclosure,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalHeader,
    Icon,

    /* Hooks */
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { useIntl, defineMessages } from 'react-intl'
import { formatDate } from '../../../utils/utils'

/* Hooks */
import { useCurrentCustomer } from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import { useLoyalty } from '../../../integrations/loyalty/use-loyalty'
import { useOptimizely } from '../../../contexts/optimizely-context'

/* Styles */
import { useMyRewardsStyles } from '../styles/myRewardsStyles'

/* Icons */
import { CustomClose, RewardsTransaction, InfoCircle, QRCodeIcon } from '../../../components/icons'

/* Utils */
import { generateStyles } from '../../../components/optimizely/utils/utils'
import { renderComponentsSwitch } from '../../../components/optimizely/utils/contentRenderer'

import CollectifIdModal from './collectif-id-modal'

/* Static resources and constants */
const staticResources = defineMessages({
    greeting: {
        id: 'my_rewards.greeting',
        defaultMessage: 'Hi, {firstName}'
    },
    fallbackGreeting: {
        id: 'my_rewards.fallback_greeting',
        defaultMessage: 'Hi, Babe'
    },
    rewardsAnchor: {
        id: 'my_rewards.anchor.rewards',
        defaultMessage: 'Rewards'
    },
    earnPointsAnchor: {
        id: 'my_rewards.anchor.earn_points',
        defaultMessage: 'Earn Points'
    },
    benefitsAnchor: {
        id: 'my_rewards.anchor.benefits',
        defaultMessage: 'Benefits'
    },
    redeemablePointsTitle: {
        id: 'my_rewards.redeemable_points_title',
        defaultMessage: 'Redeemable points earned since first joining the Collectif'
    },
    tierPoints: {
        id: 'my_rewards.tier_points',
        defaultMessage: 'Points earned since first joining the Collectif'
    },
    pointsHistory: {
        id: 'my_rewards.points_history',
        defaultMessage: 'Points history'
    },
    yourLevel: {
        id: 'my_rewards.your_level',
        defaultMessage: 'Your level is'
    },
    collectifStatus: {
        id: 'my_rewards.collectif_status',
        defaultMessage: 'Collectif status based on your purchases over the last year'
    },
    creatorLevel: {
        id: 'my_rewards.creator_level',
        defaultMessage: 'Creator'
    },
    museLevel: {
        id: 'my_rewards.muse_level',
        defaultMessage: 'Muse'
    },
    iconLevel: {
        id: 'my_rewards.icon_level',
        defaultMessage: 'Icon'
    },
    loyaltyFooterText: {
        id: 'my_rewards.loyalty_footer_text',
        defaultMessage: "You're missing {missingQualifyingPoints} qualifying points until you've reached Collectif {nextTier} status."
    },
    loyaltyFooterSubText: {
        id: 'my_rewards.loyalty_footer_subtext',
        defaultMessage: 'Earn before {dueDate}'
    },
    drawerTitle: {
        id: 'my_rewards.history.drawer_title',
        defaultMessage: 'Points History'
    },
    drawerPoints: {
        id: 'my_rewards.history.drawer_points',
        defaultMessage: '{loyaltyPoints} Points'
    },
    drawerResetDate: {
        id: 'my_rewards.history.drawer_reset_date',
        defaultMessage: 'Your Loyalty status will reset on {resetDate}.'
    },
    drawerPointsAdded: {
        id: 'my_rewards.history.drawer_points_added',
        defaultMessage: '+ {points} pts'
    },
    drawerPointsSpent: {
        id: 'my_rewards.history.drawer_points_spent',
        defaultMessage: '- {points} pts'
    }
})

const LoyaltyStatus = ({ loyaltyData, showGauge = false }) => {
    const { rewardPageBlock } = useOptimizely()
    const styles = useMyRewardsStyles()
    const intl = useIntl()
    const isMobile = useBreakpointValue({ base: true, lg: false })
    const loyaltyStatus = styles?.components?.loyaltyStatus
    const accountBlock = styles?.components?.accountBlock

    const progressBarEnabled = loyaltyData?.progressBarEnabled ?? true
    const maxStatusPercentage = Math.min(loyaltyData?.membershipStatus?.maxStatusPercentage ?? 0, 100)

    const labels = rewardPageBlock?.CollectifStatusList || [
        intl.formatMessage(staticResources.creatorLevel),
        intl.formatMessage(staticResources.museLevel),
        intl.formatMessage(staticResources.iconLevel),
    ]

    /* When sflmEnableProgressBar is off: hide only the progress bar and tier labels; title + tier name + line below stay */
    return (
        <Box>
            {/* Title and tier name – always shown */}
            {rewardPageBlock && rewardPageBlock?.CollectifStatusText ? (
                <Text {...generateStyles(rewardPageBlock?.CollectifStatusTextStyle, {...accountBlock.loyaltyText})}>
                    {rewardPageBlock?.CollectifStatusText}
                </Text>
            ) : (
                <Text {...accountBlock.loyaltyText}>
                    {intl.formatMessage(staticResources.collectifStatus)}
                </Text>
            )}
            <Text {...accountBlock.loyaltyTitle}>
                {loyaltyData?.membershipStatus?.status}
            </Text>

            {/* Progress bar and Creator/Muse/Icon labels – only when sflmEnableProgressBar is enabled */}
            {progressBarEnabled && (
                <Box {...loyaltyStatus.progressBarContainer}>
                    <Box {...loyaltyStatus.progressBarWrapper}>
                        <Box
                            {...loyaltyStatus.progressFill}
                            w={`${maxStatusPercentage}%`}
                        />
                    </Box>

                    <Flex {...loyaltyStatus.labelsRow}>
                        {labels.map((label, idx) => {
                            let showArrow = false
                            let alignLabel = 'center'
                            if (idx === 0) { showArrow = true; alignLabel = 'left' }
                            if (idx === 1 && maxStatusPercentage >= 50) showArrow = true
                            if (idx === 2 && maxStatusPercentage === 100) showArrow = true
                            if (idx === 2) { alignLabel = 'right' }

                            return (
                                <Box key={label} {...loyaltyStatus.labelWrapper}>
                                    <Text {...loyaltyStatus.labelText} align={alignLabel}>{label}</Text>
                                    {showGauge && showArrow && (
                                        <Box
                                            {...loyaltyStatus.arrow}
                                            {...(idx === 0
                                                ? loyaltyStatus.arrowLeft
                                                : idx === labels.length - 1
                                                    ? loyaltyStatus.arrowRight
                                                    : loyaltyStatus.arrowCenter)}
                                        />
                                    )}
                                </Box>
                            )
                        })}
                    </Flex>
                </Box>
            )}
        </Box>
    )
}

LoyaltyStatus.propTypes = {
    loyaltyData: PropTypes.object
}

const AccountBlock = ({ loyaltyData, isLoyaltyDataLoading, exclusiveRewardsRef, loyaltyProgramRef, earningPointsRef }) => {
    const { rewardPageBlock } = useOptimizely()
    const styles = useMyRewardsStyles()
    const intl = useIntl()
    const isMobile = useBreakpointValue({ base: true, lg: false })
    const { transactionJournal, isLoadingTransactions } = useLoyalty()
    const { data: customer } = useCurrentCustomer()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const { isOpen: tooltipIsOpen, onOpen: tooltipOnOpen, onClose: tooltipOnClose } = useDisclosure()
    const { isOpen: collectifIdIsOpen, onOpen: collectifIdOnOpen, onClose: collectifIdOnClose } = useDisclosure()
    const [transactionData, setTransactionData] = useState()
    const accountBlock = styles?.components?.accountBlock
    const progressBarEnabled = loyaltyData?.progressBarEnabled ?? true
    const TOGGLE_CONTENT = {
        // GDI Told us will not be used at this point. Will remain only CMS and if they want to configure it will do it there
        showAnchorFallback: false
    }

    // This will fallback CMS
    const hardCodedData = {
        tooltipModal: {
            title: 'How to move up in the garage collectif',
            text1: "You've started earning points and now you're wondering, how do I move up to the next level? Each tier requires a certain amount of points before you can qualify. So, as soon as you've reached enough points to qualify for that next tier, you'll move up right away!",
            text2: "On the anniversary of your join date, we'll see how many points you've earned in the last year. That will determine whether or not you stay at your current tier or move down to whichever tier matches your number of points. You can redeem your points for rewards whenever you want, but don't worry, this won't affect your qualifying points, meaning the ones we count to decide which level you're at!"
        }
    }

    // TODO FD REWARDS: Now the mapping is based on the CtaLinkText we should have a separate fixed value for this
    const anchorMappingToCMS = {
        "REWARDS": exclusiveRewardsRef,
        "EARN POINTS": earningPointsRef,
        "BENEFITS": loyaltyProgramRef
    }

    const scrollToSection = (ref) => {
        const element = ref.current;
        if (element) {
            window.scrollTo({
                top: element.offsetTop + 50, // Header offset
                behavior: 'smooth'
            });
        }
    }

    const handleOpenHistoryPoints = async () => {
        onOpen()
        setTransactionData(await transactionJournal())
    }

    const handleLoyaltyInfo = () => {
        tooltipOnOpen()
    }

    return (
        <Box {...styles.componentWrapper} {...accountBlock.container}>
            {/* Title */}
            {rewardPageBlock && rewardPageBlock?.Salutation ? (
                <Text {...generateStyles(rewardPageBlock?.SalutationTextStyle, {...accountBlock.title})}>
                    {customer?.firstName ? (
                        <>
                            {rewardPageBlock?.Salutation} {customer?.firstName}
                        </>
                    ) : (
                        intl.formatMessage(staticResources.fallbackGreeting)
                    )}
                </Text>
            ) : (
                <Text {...accountBlock.title}>
                    {customer?.firstName ? (
                        intl.formatMessage(staticResources.greeting, { firstName: customer?.firstName })
                    ) : (
                        intl.formatMessage(staticResources.fallbackGreeting)
                    )}
                </Text>
            )}

            {/* Loyalty QR */}
            <Box
                display={{ base: 'flex', lg: 'none' }}
                my={6}
                alignItems="center"
                justifyContent="center"
                gap={2}
                cursor="pointer"
                onClick={collectifIdOnOpen}
            >
                <QRCodeIcon />
                <Box>{customer?.c_b2cloyalty_loyaltyProgramMemberNumber}</Box>
            </Box>

            {/* Loyalty QR drawer */}
            <CollectifIdModal
                isOpen={collectifIdIsOpen}
                onClose={collectifIdOnClose}
                memberNumber={customer?.c_b2cloyalty_loyaltyProgramMemberNumber}
            />

            {/* Page anchors */}
            {((rewardPageBlock && rewardPageBlock?.RewardCTAs) ||
                TOGGLE_CONTENT.showAnchorFallback) && (
                <Flex {...accountBlock.anchorsContainer}>
                    {rewardPageBlock && rewardPageBlock?.RewardCTAs ? (
                        rewardPageBlock?.RewardCTAs?.map((ctaItemBlock) => {
                            return renderComponentsSwitch(ctaItemBlock?.ContentLink?.Expanded, {
                                stylingButtonOverwrite: {...accountBlock.anchor},
                                isTrigger: true,
                                onClickCtaItemBlock: () => {
                                    scrollToSection(anchorMappingToCMS[ctaItemBlock?.ContentLink?.Expanded?.CtaLinkText])
                                }
                            })
                        })
                    ) : (
                        TOGGLE_CONTENT.showAnchorFallback &&
                            <>
                                <Button variant={'outline'} {...accountBlock.anchor} onClick={() => scrollToSection(exclusiveRewardsRef)}>
                                    <Text as='span'>
                                        {intl.formatMessage(staticResources.rewardsAnchor)}
                                    </Text>
                                </Button>
                                <Button variant={'outline'} {...accountBlock.anchor} onClick={() => scrollToSection(earningPointsRef)}>
                                    <Text as='span'>
                                        {intl.formatMessage(staticResources.earnPointsAnchor)}
                                    </Text>
                                </Button>
                                <Button variant={'outline'} {...accountBlock.anchor} onClick={() => scrollToSection(loyaltyProgramRef)}>
                                    <Text as='span'>
                                        {intl.formatMessage(staticResources.benefitsAnchor)}
                                    </Text>
                                </Button>
                            </>
                    )}
                </Flex>
            )}

            {/* Loyalty info */}
            <Skeleton isLoaded={!isLoyaltyDataLoading}>
                <Box {...accountBlock.loyaltyInfoContainer}>
                    <Box {...accountBlock.loyaltyHeader}>
                        {rewardPageBlock && rewardPageBlock?.RedeemablePointsText ? (
                            <Text {...generateStyles(rewardPageBlock?.RedeemablePointsTextStyle, {...accountBlock.loyaltySubText})}>
                                {rewardPageBlock?.RedeemablePointsText}
                            </Text>
                        ) : (
                            <Text {...accountBlock.loyaltySubText}>
                                {intl.formatMessage(isMobile ? staticResources.tierPoints : staticResources.redeemablePointsTitle)}
                            </Text>
                        )}
                        <Flex {...accountBlock.loyaltyPointsBlock}>
                            <Text {...accountBlock.loyaltyTitle}>
                                {loyaltyData?.pointsAndCashDiscount?.nonQualifyingPointsBalance}
                            </Text>
                             {rewardPageBlock && rewardPageBlock?.RedeemablePointsText ? (
                                rewardPageBlock?.PointsHistoryCTA?.map((ctaItemBlock) => {
                                    return renderComponentsSwitch(ctaItemBlock?.ContentLink?.Expanded, {
                                        stylingTextOverwrite: {...accountBlock.loyaltyLink},
                                        isTrigger: true,
                                        onClickCtaItemBlock: () => {
                                            handleOpenHistoryPoints()
                                        }
                                    })
                                })
                             ) : (
                                <Button variant={'link'} {...accountBlock.loyaltyLink} onClick={handleOpenHistoryPoints}>
                                    {intl.formatMessage(staticResources.pointsHistory)}
                                </Button>
                             )}
                        </Flex>
                    </Box>
                    <Box {...accountBlock.loyaltyContent}>
                        <LoyaltyStatus loyaltyData={loyaltyData} />
                    </Box>
                    <Box {...accountBlock.loyaltyFooter}>
                        {loyaltyData?.nextTier && loyaltyData?.nextTier?.missingPoints && loyaltyData?.nextTier?.statusName &&
                            <>
                                {rewardPageBlock && rewardPageBlock?.CollectifStatusMissingText ? (
                                    <Text {...generateStyles(rewardPageBlock?.CollectifStatusMissingTextStyle, {...accountBlock.loyaltyFooterText})}>
                                        {rewardPageBlock?.CollectifStatusMissingText.replace(/#/g, loyaltyData?.nextTier?.missingPoints).replace(/x/g, loyaltyData?.nextTier?.statusName.charAt(0).toUpperCase() + loyaltyData?.nextTier?.statusName.slice(1))}
                                    </Text>
                                ) : (
                                    <Text {...accountBlock.loyaltyFooterText}>
                                        {intl.formatMessage(staticResources.loyaltyFooterText, {
                                            missingQualifyingPoints: loyaltyData?.nextTier?.missingPoints,
                                            nextTier: loyaltyData?.nextTier?.statusName.charAt(0).toUpperCase() + loyaltyData?.nextTier?.statusName.slice(1)
                                        })}
                                    </Text>
                                )}
                                <Text {...accountBlock.loyaltyFooterSubText}>
                                    {rewardPageBlock && rewardPageBlock?.CollectifStatusMissingSinceText ? (
                                        rewardPageBlock?.CollectifStatusMissingSinceText.replace(/XX\/XX\/XX/g, loyaltyData?.nextTier?.dueDate)
                                    ) : (
                                        intl.formatMessage(staticResources.loyaltyFooterSubText, {
                                            dueDate: loyaltyData?.nextTier?.dueDate
                                        })
                                    )}
                                    <IconButton
                                        variant="unstyled"
                                        h={'auto'}
                                        minW={'auto'}
                                        onClick={handleLoyaltyInfo}
                                        {...accountBlock.infoCircle}
                                        icon={<InfoCircle boxSize="16px" />}
                                        aria-label="Rewards info"
                                    />
                                </Text>
                            </>
                        }
                    </Box>
                </Box>
            </Skeleton>

            {/* History points drawer */}
            <Drawer
                placement="right"
                isOpen={isOpen}
                onClose={onClose}
                onOpen={onOpen}
                size="md"
                scrollBehavior="inside"
                isFullHeight={true}
            >
                <DrawerOverlay />
                <DrawerContent {...accountBlock.drawerContent} >
                    <IconButton
                        aria-label="Close"
                        icon={<CustomClose />}
                        {...accountBlock.drawerCloseIcon}
                        onClick={onClose}
                    />
                    <DrawerBody {...accountBlock.drawerBody}>
                        <Text {...accountBlock.drawerTitle}>
                            {intl.formatMessage(staticResources.drawerTitle)}
                        </Text>
                        <Text {...accountBlock.drawerPointsTitle}>
                            {intl.formatMessage(staticResources.drawerPoints, {
                                loyaltyPoints: loyaltyData?.pointsAndCashDiscount?.nonQualifyingPointsBalance
                            })}
                        </Text>
                        <Text {...accountBlock.drawerResetDate}>
                            {intl.formatMessage(staticResources.drawerResetDate, {
                                resetDate: formatDate(loyaltyData?.nextTier?.dueDate)
                            })}
                        </Text>
                        {isLoadingTransactions ? (
                            <Skeleton {...accountBlock.transactionContainer} height='75%' width='100%' />
                        ) : (
                            <Box {...accountBlock.transactionContainer}>
                                {transactionData?.map((transaction, tIndex) => {
                                    return (
                                        <Box {...accountBlock.transactionBlock} key={`transaction-${tIndex}`}>
                                            <Text {...accountBlock.transactionDate}>
                                                {new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(transaction?.transactionDate))}
                                            </Text>
                                            <Flex {...accountBlock.transactionCard}>
                                                <Flex {...accountBlock.transactionInfo}>
                                                    <RewardsTransaction />
                                                    <Text {...accountBlock.purchase}>
                                                        {transaction?.transactionDetails}
                                                    </Text>
                                                </Flex>
                                                <Text {...accountBlock.transactionPoints}>
                                                    {intl.formatMessage(transaction?.nonQualifyingPoints > 0 ? staticResources.drawerPointsAdded : staticResources.drawerPointsSpent, {
                                                        points: Math.abs(transaction?.nonQualifyingPoints)
                                                    })}
                                                </Text>
                                            </Flex>
                                        </Box>
                                    )
                                })}
                            </Box>
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Tooltip modal */}
            <Modal
                isOpen={tooltipIsOpen}
                onClose={tooltipOnClose}
                motionPreset="slideInBottom"
            >
                <ModalOverlay />
                <ModalContent
                    {...accountBlock.modalContent}
                    borderRadius={0}
                    position={isMobile ? 'fixed' : 'relative'}
                    bottom={isMobile ? '0' : 'auto'}
                    m={isMobile ? '0' : 'auto'}
                >
                    <ModalHeader {...accountBlock.modalHeader}>
                        <IconButton
                            {...accountBlock.modalIcon}
                            onClick={tooltipOnClose}
                            icon={<Icon as={CustomClose} />}
                            aria-label="Close modal"
                            variant="ghost"
                            size="sm"
                        />
                    </ModalHeader>
                    <ModalBody {...accountBlock.modalBody}>
                        <Text {...accountBlock.modalTitle}>
                            {hardCodedData.tooltipModal.title}
                        </Text>
                        <Text {...accountBlock.modalText}>
                            {hardCodedData.tooltipModal.text1}
                        </Text>
                        <Text {...accountBlock.modalText}>
                            {hardCodedData.tooltipModal.text2}
                        </Text>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    )
}

AccountBlock.propTypes = {
    loyaltyData: PropTypes.object,
    isLoyaltyDataLoading: PropTypes.bool,
    exclusiveRewardsRef: PropTypes.object,
    loyaltyProgramRef: PropTypes.object,
    earningPointsRef: PropTypes.object
}

export default AccountBlock
